import React, { useState, useCallback, useRef } from 'react';
import { Button, Upload, Card, Badge, Alert, Space, Popconfirm, Select, App, Modal } from 'antd';
import { UploadOutlined, InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { 
  DOCUMENT_TYPES, 
  DOCUMENT_GROUPS,
  getDocumentTypeById, 
  getDocumentGroupById,
  getGroupedDocumentTypes,
  validateFile, 
  willReplaceExisting, 
  allowsMultipleFiles 
} from '../config/documentTypes';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const { Dragger } = Upload;
const { Option, OptGroup } = Select;

const DocumentUpload = ({ 
  projectId = null, 
  onUploadSuccess, 
  onUploadError, 
  className = '' 
}) => {
  const [selectedType, setSelectedType] = useState(DOCUMENT_TYPES.find(t => t.id === null) || DOCUMENT_TYPES[0]); // Default to general or first type
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { getAuthToken } = useAuth();
  const { message } = App.useApp();

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    console.log('File selected:', file?.name);
    const token = getAuthToken();
    console.log('Token available:', !!token);
    console.log('Selected type:', selectedType);

    if (!token) {
      message.error('Authentication token not found. Please log in again.');
      return false;
    }

    if (!selectedType) {
      message.error('Please select a document type first.');
      return false;
    }

    // Validate file
    const validationError = validateFile(file, selectedType);
    if (validationError) {
      message.error(validationError);
      if (onUploadError) {
        onUploadError({ error: validationError });
      }
      return false; // Prevent upload
    }

    // Show confirmation for replacement types
    if (willReplaceExisting(selectedType) && selectedType.id) {
      console.log('Showing replacement confirmation for:', selectedType.label);
      const shouldContinue = await new Promise((resolve) => {
        Modal.confirm({
          title: <span style={{ color: '#fff' }}>Replace Existing Document</span>,
          content: (
            <div style={{ color: '#fff' }}>
              Uploading this {selectedType.label} document will replace any existing document of this type. Do you want to continue?
            </div>
          ),
          okText: 'Continue',
          cancelText: 'Cancel',
          className: 'dark-modal',
          onOk: () => {
            console.log('User clicked Continue');
            resolve(true);
          },
          onCancel: () => {
            console.log('User clicked Cancel');
            resolve(false);
          },
        });
      });

      if (!shouldContinue) {
        console.log('User cancelled upload');
        return false; // User cancelled
      }
      console.log('User confirmed upload, proceeding...');
    } else {
      console.log('No replacement confirmation needed, proceeding with upload');
    }

    // Perform upload directly if no replacement warning needed or user confirmed
    try {
      console.log('About to call performUpload with type:', selectedType.id);
      await performUpload(file, token);
    } catch (error) {
      console.error('Upload failed in handleFileSelect:', error);
    }
    
    return false; // Prevent default upload behavior
  }, [selectedType, projectId, onUploadSuccess, onUploadError]);

  const performUpload = async (file, token) => {
    console.log('Starting upload for:', file?.name);
    console.log('Upload params:', { 
      fileName: file?.name, 
      hasToken: !!token, 
      projectId, 
      documentType: selectedType?.id 
    });
    setUploading(true);
    try {
      const response = await apiService.uploadDocument(
        file, 
        token, 
        projectId, 
        selectedType.id || 'general'
      );
      console.log('Upload response:', response);

      message.success(`"${file.name}" uploaded successfully!`);

      // Show replacement notification
      if (response.replaced_documents > 0) {
        const replacedFiles = response.replaced_document_details
          ?.map(doc => doc.filename)
          .join(', ') || 'existing documents';
        
        message.info(`Replaced: ${replacedFiles}`);
      }

      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

    } catch (error) {
      console.error('Upload failed:', error);
      message.error(`Upload failed: ${error.message}`);
      
      if (onUploadError) {
        onUploadError({ error: error.message });
      }
    } finally {
      setUploading(false);
    }
  };

  // Create grouped options for document type select
  const renderDocumentTypeOptions = () => {
    const groupedTypes = getGroupedDocumentTypes();
    
    return groupedTypes.map(group => (
      <OptGroup key={group.id} label={
        <span style={{ color: group.color }}>
          {group.icon} {group.label}
        </span>
      }>
        {group.types.map(type => (
          <Option key={type.id || 'general'} value={type.id || 'general'}>
            <Space>
              <span>{type.icon}</span>
              <span>{type.label}</span>
              {willReplaceExisting(type) && type.id && (
                <Badge 
                  status="warning" 
                  text="Replaces existing" 
                  style={{ fontSize: '11px' }}
                />
              )}
            </Space>
          </Option>
        ))}
      </OptGroup>
    ));
  };

  const currentGroup = getDocumentGroupById(selectedType?.group || 'general');

  return (
    <div className={`enhanced-document-upload ${className}`}>
      {/* Document Type Selection */}
      <Card 
        size="small" 
        title="Document Type" 
        className="document-type-selector"
        style={{ marginBottom: 16 }}
      >
        <Select
          value={selectedType?.id || 'general'}
          onChange={(value) => {
            console.log('Select onChange - value:', value);
            const type = getDocumentTypeById(value === 'general' ? null : value);
            console.log('Found document type:', type);
            setSelectedType(type);
          }}
          style={{ width: '100%' }}
          placeholder="Select document type"
          disabled={uploading}
        >
          {renderDocumentTypeOptions()}
        </Select>
        
        {selectedType && (
          <div style={{ marginTop: 12 }}>
            <Alert
              message={
                <span style={{ color: currentGroup?.color }}>
                  {selectedType.icon} {selectedType.label}
                </span>
              }
              description={
                <div>
                  <div>{selectedType.description}</div>
                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                    <strong>Supported formats:</strong> {selectedType.allowedExtensions.join(', ')}
                  </div>
                  {willReplaceExisting(selectedType) && selectedType.id && (
                    <div style={{ fontSize: '12px', color: '#faad14', marginTop: 4 }}>
                      ⚠️ This will replace any existing {selectedType.label} document
                    </div>
                  )}
                </div>
              }
              type="info"
              showIcon={false}
              style={{ 
                backgroundColor: currentGroup?.color + '10',
                borderColor: currentGroup?.color + '40'
              }}
            />
          </div>
        )}
      </Card>

      {/* Upload Area */}
      <Card 
        size="small" 
        title={`Upload ${selectedType?.label || 'Document'}`}
        className="upload-area-card"
      >
        <Dragger
          name="file"
          multiple={false}
          beforeUpload={handleFileSelect}
          accept={selectedType?.allowedExtensions?.join(',')}
          disabled={uploading}
          showUploadList={false}
          className={uploading ? 'uploading' : ''}
        >
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              <InboxOutlined style={{ color: currentGroup?.color || '#8b5cf6' }} />
            </div>
            
            {uploading ? (
              <div>
                <p><strong>Uploading {selectedType?.label}...</strong></p>
                <p style={{ color: '#6b7280' }}>Please wait while your document is being processed</p>
              </div>
            ) : (
              <div>
                <p>
                  <strong>Drop your {selectedType?.label || 'document'} here</strong>
                </p>
                <p style={{ color: '#6b7280' }}>
                  or <Button type="link" style={{ padding: 0, height: 'auto' }}>browse files</Button>
                </p>
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '12px' }}>
                  Supported: {selectedType?.allowedExtensions?.join(', ') || 'Various formats'}
                </p>
                {willReplaceExisting(selectedType) && selectedType?.id && (
                  <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '8px' }}>
                    ⚠️ This will replace any existing {selectedType.label} document
                  </p>
                )}
              </div>
            )}
          </div>
        </Dragger>
      </Card>
    </div>
  );
};

export default DocumentUpload;